// Helpers for building srcset attributes for remote images we can't run
// through vite-imagetools — namely existing Supabase Storage images
// and Unsplash stock photos.

type Format = "webp" | "avif" | "jpg" | "png";

// === Supabase Storage =====================================================
//
// Supabase has TWO image endpoints:
//   1. /storage/v1/object/public/<bucket>/<key>
//      Always available on every plan — serves the original file as-is.
//   2. /storage/v1/render/image/public/<bucket>/<key>?width=X&format=webp
//      Paid-plan (Pro+) "Image Transformations" — resizes + re-encodes.
//
// Phase 2 of the perf remediation wired every Supabase <img> through #2,
// but this project's Supabase plan doesn't have Image Transformations
// enabled, so every transformed URL 404s and the browser falls back to
// alt text (user-visible as "all images missing, alt text placeholders").
//
// Default behavior is now: return the original object URL unchanged, so
// images render reliably. If/when the project upgrades to Pro and enables
// Image Transformations, set
//     VITE_SUPABASE_IMAGE_TRANSFORMS=true
// in the build env and this helper will start emitting per-width WebP
// requests again.
//
// Reference:
// https://supabase.com/docs/guides/storage/serving/image-transformations

const SUPABASE_IMAGE_TRANSFORMS_ENABLED =
  import.meta.env.VITE_SUPABASE_IMAGE_TRANSFORMS === "true";

function toSupabaseRenderUrl(url: string): string {
  return url.replace(
    "/storage/v1/object/public/",
    "/storage/v1/render/image/public/",
  );
}

export function supabaseImgSrc(
  url: string,
  opts: { width: number; format?: Format; quality?: number } = { width: 1024 },
): string {
  if (!url.includes("/storage/v1/object/public/")) return url;
  if (!SUPABASE_IMAGE_TRANSFORMS_ENABLED) return url; // serve original
  const render = toSupabaseRenderUrl(url);
  const params = new URLSearchParams();
  params.set("width", String(opts.width));
  if (opts.format) params.set("format", opts.format);
  if (opts.quality) params.set("quality", String(opts.quality));
  return `${render}?${params.toString()}`;
}

export function supabaseSrcSet(
  url: string,
  widths: number[],
  format?: Format,
): string {
  // When transforms are off we can't vary the src by width — the original
  // is always returned — so don't emit a multi-width srcset at all. The
  // consuming <img> will just fall back to `src`.
  if (!SUPABASE_IMAGE_TRANSFORMS_ENABLED) return "";
  return widths
    .map((w) => `${supabaseImgSrc(url, { width: w, format, quality: 80 })} ${w}w`)
    .join(", ");
}

// === Unsplash =============================================================
//
// Unsplash's own CDN always supports ?w=X&fm=webp&q=80 (and auto-webp via
// &auto=format) on every photo URL, regardless of plan, so we keep the
// transformed srcset.

export function unsplashImgSrc(
  url: string,
  opts: { width: number; format?: Format; quality?: number } = { width: 1024 },
): string {
  const u = new URL(url);
  u.searchParams.set("w", String(opts.width));
  u.searchParams.set("q", String(opts.quality ?? 80));
  if (opts.format) {
    u.searchParams.set("fm", opts.format);
  } else {
    u.searchParams.set("auto", "format");
  }
  // Unsplash `fit=crop` preserves aspect ratio at the requested width.
  if (!u.searchParams.has("fit")) u.searchParams.set("fit", "crop");
  return u.toString();
}

export function unsplashSrcSet(
  url: string,
  widths: number[],
  format?: Format,
): string {
  return widths
    .map((w) => `${unsplashImgSrc(url, { width: w, format, quality: 80 })} ${w}w`)
    .join(", ");
}

// Default width presets for common image roles. Kept here so every call site
// uses the same ladder; widths are chosen to match DPR-aware breakpoints on
// mobile (375 → 750 @2x), tablet (768 → 1536 @2x), and desktop (1200+).
export const WIDTH_PRESETS = {
  hero: [640, 1024, 1440, 1920],
  wide: [480, 768, 1024, 1440],
  card: [320, 640, 960],
  portrait: [160, 320, 480],
  logo: [120, 240, 360],
} as const;
